"use client"

import type React from "react"
import { useState, useEffect, useRef, useCallback } from "react"
import styles from "./InfiniteScrollSelect.module.scss";

export interface SelectOption {
  id: string
  label: string
  avatar?: string
}

interface InfiniteScrollSelectProps {
  label?: string
  options: SelectOption[]
  value: SelectOption | null
  onChange: (option: SelectOption) => void
  placeholder?: string
  className?: string
  isLoading?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  onSearch?: (query: string) => void
  searchDebounce?: number
  emptyMessage?: string
  assignLabel?: string
}

export default function InfiniteScrollSelect({
  label = "Assigned Solar Provider",
  options,
  value,
  onChange,
  placeholder = "Select an option",
  className = "",
  isLoading = false,
  hasMore = false,
  onLoadMore,
  onSearch,
  searchDebounce = 300,
  emptyMessage = "No options available",
  assignLabel = "Assign to",
}: Readonly<InfiniteScrollSelectProps>) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [localOptions, setLocalOptions] = useState<SelectOption[]>(options)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchTimeout = useRef<NodeJS.Timeout | null>(null)
  const observer = useRef<IntersectionObserver | null>(null)
  const lastOptionRef = useRef<HTMLDivElement | null>(null)
  const loadingRef = useRef<boolean>(false)

  // Update local options when props options change
  useEffect(() => {
    setLocalOptions(options)
  }, [options])

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Track loading state to prevent multiple calls
  useEffect(() => {
    loadingRef.current = isLoading
  }, [isLoading])

  // Handle infinite scroll with Intersection Observer
  const lastOptionCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (loadingRef.current || !hasMore) return

      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && onLoadMore && !loadingRef.current) {
            loadingRef.current = true // Set loading flag to prevent multiple calls
            onLoadMore()
          }
        },
        {
          rootMargin: "100px", // Load earlier before reaching the end
          threshold: 0.1,
        },
      )

      if (node) {
        lastOptionRef.current = node
        observer.current.observe(node)
      }
    },
    [hasMore, onLoadMore],
  )

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchTerm(query)

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }

    // Filter local options immediately
    if (!onSearch) {
      const filtered = options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()))
      setLocalOptions(filtered)
    }

    // Debounce remote search
    if (onSearch) {
      searchTimeout.current = setTimeout(() => {
        onSearch(query)
      }, searchDebounce)
    }
  }

  // Toggle dropdown
  const toggleDropdown = () => {
    const newIsOpen = !isOpen
    setIsOpen(newIsOpen)

    if (newIsOpen) {
      // Focus the search input when opening
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }, 0)
    }
  }

  // Select an option
  const handleSelect = (option: SelectOption) => {
    onChange(option)
    setIsOpen(false)
    setSearchTerm("")
  }

  return (
    <div className={`${styles.selectContainer} ${className}`} ref={containerRef}>
      {label && <label className={styles.selectLabel}>{label}</label>}
      <div className={styles.selectWrapper}>
        <div className={styles.selectField} onClick={toggleDropdown}>
          <div className={styles.assignSection}>
            <svg
              className={styles.personIcon}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.5899 22C20.5899 18.13 16.7399 15 11.9999 15C7.25991 15 3.40991 18.13 3.40991 22"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{assignLabel}</span>
            <svg
              className={`${styles.chevronIcon} ${isOpen ? styles.chevronUp : ""}`}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.selectedValue}>{value?.label ?? placeholder}</div>
        </div>

        {isOpen && (
          <div className={styles.optionsDropdown} ref={dropdownRef}>
            <div className={styles.searchContainer}>
              <input
                ref={inputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className={styles.optionsList}>
              {localOptions.length > 0 ? (
                localOptions.map((option, index) => (
                  <div
                    key={option.id}
                    className={`${styles.option} ${value?.id === option.id ? styles.selected : ""}`}
                    onClick={() => handleSelect(option)}
                    ref={index === localOptions.length - 1 && hasMore ? lastOptionCallback : null}
                  >
                    {option.avatar && (
                      <span className={styles.optionAvatar}>
                        <img src={option.avatar || "/placeholder.svg"} alt={option.label} />
                      </span>
                    )}
                    <span className={styles.optionLabel}>{option.label}</span>
                  </div>
                ))
              ) : (
                <div className={styles.emptyMessage}>{isLoading ? "Loading..." : emptyMessage}</div>
              )}

              {isLoading && (
                <div className={styles.loadingIndicator}>
                  <div className={styles.spinner}></div>
                  <span>Loading more options...</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

